import React, { useState, useEffect, useRef } from 'react';
import api, { API_BASE } from '../../services/api';
import { ArtReproduction } from '../../types';
import { getArtworkDisplayUrl, useViewportWidth } from '../../hooks/useArtworkDisplayUrl';
import './ArtworkSelector.css';

interface ArtworkSelectorProps {
  value?: number;
  onChange: (artworkId: number | undefined) => void;
  placeholder?: string;
}

const ArtworkSelector: React.FC<ArtworkSelectorProps> = ({
  value,
  onChange,
  placeholder = 'Search and select an artwork...'
}) => {
  const [artworks, setArtworks] = useState<ArtReproduction[]>([]);
  const [filteredArtworks, setFilteredArtworks] = useState<ArtReproduction[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedArtwork, setSelectedArtwork] = useState<ArtReproduction | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const viewportWidth = useViewportWidth();

  useEffect(() => {
    const fetchArtworks = async (): Promise<void> => {
      setLoading(true);
      try {
        const response = await api.get<ArtReproduction[]>('/art');
        setArtworks(response.data);
        setFilteredArtworks(response.data);
      } catch (error) {
        console.error('Failed to fetch artworks:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArtworks();
  }, []);

  useEffect(() => {
    if (value && artworks.length > 0) {
      const artwork = artworks.find(a => a.id === value);
      setSelectedArtwork(artwork || null);
      if (artwork) {
        setSearchQuery(artwork.title);
      }
    } else {
      setSelectedArtwork(null);
      if (!isOpen) {
        setSearchQuery('');
      }
    }
  }, [value, artworks, isOpen]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredArtworks(artworks);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = artworks.filter(artwork =>
        artwork.title.toLowerCase().includes(query) ||
        artwork.artistName?.toLowerCase().includes(query) ||
        artwork.id.toString().includes(query)
      );
      setFilteredArtworks(filtered);
    }
  }, [searchQuery, artworks]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        if (!selectedArtwork) {
          setSearchQuery('');
        } else {
          setSearchQuery(selectedArtwork.title);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedArtwork]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchQuery(e.target.value);
    setIsOpen(true);
    if (!e.target.value) {
      setSelectedArtwork(null);
      onChange(undefined);
    }
  };

  const handleSelectArtwork = (artwork: ArtReproduction): void => {
    setSelectedArtwork(artwork);
    setSearchQuery(artwork.title);
    onChange(artwork.id);
    setIsOpen(false);
  };

  const handleClear = (): void => {
    setSelectedArtwork(null);
    setSearchQuery('');
    onChange(undefined);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleInputFocus = (): void => {
    setIsOpen(true);
  };

  const buildImageSrc = (imageUrl?: string): string | null => {
    if (!imageUrl) return null;
    return imageUrl.startsWith('http') ? imageUrl : `${API_BASE}${imageUrl}`;
  };

  return (
    <div className="artwork-selector" ref={dropdownRef}>
      <div className="artwork-selector-input-wrapper">
        <input
          ref={inputRef}
          type="text"
          className="artwork-selector-input"
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          autoComplete="off"
        />
        {selectedArtwork && (
          <button
            type="button"
            className="artwork-selector-clear"
            onClick={handleClear}
            title="Clear selection"
          >
            <i className="fas fa-times"></i>
          </button>
        )}
        <button
          type="button"
          className="artwork-selector-toggle"
          onClick={() => setIsOpen(!isOpen)}
        >
          <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`}></i>
        </button>
      </div>

      {isOpen && (
        <div className="artwork-selector-dropdown">
          {loading ? (
            <div className="artwork-selector-loading">Loading artworks...</div>
          ) : filteredArtworks.length === 0 ? (
            <div className="artwork-selector-empty">No artworks found</div>
          ) : (
            <div className="artwork-selector-list">
              {filteredArtworks.map((artwork) => (
                <div
                  key={artwork.id}
                  className={`artwork-selector-item ${selectedArtwork?.id === artwork.id ? 'selected' : ''}`}
                  onClick={() => handleSelectArtwork(artwork)}
                >
                  <div className="artwork-selector-item-image">
                    {artwork.thumbnailUrl || artwork.imageUrl ? (
                      <img
                        src={buildImageSrc(getArtworkDisplayUrl(artwork, viewportWidth)) || '/placeholder-art.png'}
                        alt={artwork.title}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          const fallback = buildImageSrc(artwork.thumbnailUrl || artwork.imageUrl);
                          if (fallback && target.src !== fallback) target.src = fallback;
                        }}
                      />
                    ) : (
                      <div className="artwork-selector-placeholder">
                        {artwork.title.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="artwork-selector-item-info">
                    <div className="artwork-selector-item-title">{artwork.title}</div>
                    {artwork.artistName && (
                      <div className="artwork-selector-item-artist">by {artwork.artistName}</div>
                    )}
                    <div className="artwork-selector-item-id">ID: {artwork.id}</div>
                  </div>
                  {selectedArtwork?.id === artwork.id && (
                    <div className="artwork-selector-item-check">
                      <i className="fas fa-check"></i>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ArtworkSelector;
